/*
 * "My Videos" (NWF-109): opens purchased Vimeo videos in the PhotoSwipe lightbox — the
 * same pattern as the PDP gallery (media-gallery.js, html dataSource).
 *
 * The entitlement gate is not here. Liquid renders only the videos the signed-in customer
 * owns; this script merely selects one of them via click or ?v=. An ID that is not in the
 * markup cannot be played, because it never reached the browser.
 *
 * Autoplay runs through contentActivate/contentDeactivate instead of the iframe src, so
 * starting and stopping playback stays fully under our control.
 */
(function () {
    'use strict';

    /** The requested handle from ?v=, or null. Defensive against broken URLs. */
    function requestedHandle(search) {
        if (typeof search !== 'string' || search === '') return null;
        try {
            const value = new URLSearchParams(search).get('v');
            return value && value.trim() !== '' ? value : null;
        } catch (error) {
            return null;
        }
    }

    /**
     * Embed URL for a Vimeo ID. Only digits are accepted: the ID comes from our own markup,
     * but it originates in an external API and ends up in an iframe src — validate instead
     * of trust.
     */
    function embedUrl(vimeoId) {
        if (typeof vimeoId !== 'string' && typeof vimeoId !== 'number') return null;
        const id = String(vimeoId).trim();
        if (!/^[0-9]+$/.test(id)) return null;
        return 'https://player.vimeo.com/video/' + id + '?autoplay=1';
    }

    /**
     * Slide HTML via DOM construction instead of string concatenation, so name and URL never
     * end up unescaped in HTML. The iframe starts on about:blank; the real URL lives in
     * data-src and is only set on contentActivate.
     *
     * The video title lives IN the slide instead of the PhotoSwipe top bar: the theme moves
     * that bar to the bottom and centers it (.pswp__top-bar in photoswipe-component.css) —
     * an extra element there pushed the close button out of the middle.
     */
    function slideHtml(entry) {
        const url = embedUrl(entry.vimeoId);
        if (!url) return null;

        const slide = document.createElement('div');
        slide.className = 'nwf-my-videos__slide';

        const player = document.createElement('div');
        player.className = 'nwf-my-videos__player';

        const iframe = document.createElement('iframe');
        iframe.src = 'about:blank';
        iframe.setAttribute('data-src', url);
        iframe.title = entry.name || 'Video';
        iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');

        player.appendChild(iframe);
        slide.appendChild(player);

        if (entry.name) {
            const title = document.createElement('h2');
            title.className = 'nwf-my-videos__slide-title';
            title.textContent = entry.name;
            slide.appendChild(title);
        }

        return slide.outerHTML;
    }

    if (customElements.get('nwf-my-videos')) return;

    class NwfMyVideos extends HTMLElement {
        connectedCallback() {
            this.entries = Array.from(this.querySelectorAll('[data-nwf-video]')).map((element) => ({
                handle: element.getAttribute('data-handle'),
                vimeoId: element.getAttribute('data-vimeo-id'),
                name: element.getAttribute('data-name') || '',
                element: element,
            }));

            // Thumbnail and the "Watch video" button both carry [data-nwf-video-open] and
            // open the same lightbox. preventDefault only on success: if opening fails
            // (PhotoSwipe not loaded), the link navigates to ?v= normally instead of the
            // click silently fizzling out.
            // AbortController instead of bare listeners: connectedCallback runs again on
            // every re-attach in the theme editor — without the signal the click handlers
            // would stack up.
            this.listenerAbort = new AbortController();
            this.entries.forEach((entry, index) => {
                entry.element.querySelectorAll('[data-nwf-video-open]').forEach((opener) => {
                    opener.addEventListener(
                        'click',
                        (event) => {
                            if (this.openAt(index)) {
                                event.preventDefault();
                            }
                        },
                        { signal: this.listenerAbort.signal },
                    );
                });
            });

            this.gateCards();
            this.handleDeepLink();
        }

        /** NWF-202 */
        gateCards() {
            if (!window.NwfVideoConsent) return;
            this.cancelCardGates = this.entries.map((entry, index) => {
                const host = entry.element.querySelector('[data-nwf-consent-card]');
                if (!host) return () => {};
                return window.NwfVideoConsent.gate(host, 'vimeo', (byClick) => {
                    if (byClick) this.openAt(index);
                });
            });
        }

        disconnectedCallback() {
            if (this.cancelCardGates) {
                this.cancelCardGates.forEach((cancel) => cancel());
                this.cancelCardGates = null;
            }
            // Relevant in the theme editor: the section reload replaces the DOM — without
            // destroy, the lightbox instance and listeners would cling to the orphaned element.
            if (this.listenerAbort) {
                this.listenerAbort.abort();
                this.listenerAbort = null;
            }
            if (this.lightbox) {
                this.lightbox.destroy();
                this.lightbox = null;
            }
        }

        /**
         * ?v= from the banner link or a shared URL: open the matching video directly. A
         * handle that is not in the list either does not belong to the customer or the link
         * is stale — the warning box says so instead of silently doing nothing.
         */
        handleDeepLink() {
            const handle = requestedHandle(window.location.search);
            if (!handle) return;

            const index = this.entries.findIndex((entry) => entry.handle === handle);
            if (index === -1) {
                this.showUnknownNotice();
                return;
            }

            if (this.openAt(index)) return;

            if (!window.FoxTheme || !window.FoxTheme.PhotoSwipeLightbox) {
                // photoswipe.js has not executed yet (or sets its global asynchronously).
                // A single retry after window.load; no further polling after that.
                window.addEventListener(
                    'load',
                    () => {
                        if (!this.openAt(index)) this.showUnknownNotice();
                    },
                    { once: true },
                );
            } else {
                // The card exists but there is no slide (unusable Vimeo ID) — feedback
                // instead of a click into the void.
                this.showUnknownNotice();
            }
        }

        showUnknownNotice() {
            const unknown = this.querySelector('[data-nwf-video-unknown]');
            if (unknown) unknown.hidden = false;
        }

        /**
         * Opens the clicked video as a single slide. Returns true on success.
         *
         * Deliberately exactly ONE slide per open: no sliding through to the other videos —
         * only what was clicked opens. The dataSource is therefore set freshly on every
         * open with just this one video.
         */
        openAt(index) {
            if (!window.FoxTheme || !window.FoxTheme.PhotoSwipeLightbox) return false;

            const entry = this.entries[index];
            const html = entry ? slideHtml(entry) : null;
            if (!html) return false;

            this.ensureLightbox();
            if (!this.lightbox) return false;

            this.activeHandle = entry.handle;
            this.lightbox.options.dataSource = [{ html: html }];
            this.lightbox.loadAndOpen(0);
            return true;
        }

        setUrlParam(handle) {
            try {
                const url = new URL(window.location.href);
                if (handle) {
                    url.searchParams.set('v', handle);
                } else {
                    url.searchParams.delete('v');
                }
                window.history.replaceState({}, '', url);
            } catch (error) {
                // URL housekeeping is comfort, not a feature — an error here must not stop anything.
            }
        }

        /**
         * Creates the lightbox instance once. The dataSource does NOT go here — openAt sets
         * it per open with exactly one slide, which is also why there are no arrow buttons
         * and no slide navigation.
         */
        ensureLightbox() {
            if (this.lightbox) return;

            this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
                pswpModule: window.FoxTheme.PhotoSwipe,
                bgOpacity: 1,
                arrowPrev: false,
                arrowNext: false,
                zoom: false,
                close: false,
                counter: false,
                preloader: false,
            });

            this.lightbox.on('contentActivate', ({ content }) => {
                this.togglePlayback(content, true);
                this.setUrlParam(this.activeHandle);
            });

            this.lightbox.on('contentDeactivate', ({ content }) => {
                this.togglePlayback(content, false);
            });

            this.lightbox.on('destroy', () => {
                this.setUrlParam(null);
            });

            // Disable swipe gestures entirely: there is only one slide, the gesture just made
            // the video spring back and forth. Same move as in media-gallery.js, where
            // non-image slides also suppress dragging via pointerDown.
            this.lightbox.on('pointerDown', (event) => {
                event.preventDefault();
            });

            // Custom close button like in media-gallery.js, so the lightbox looks exactly
            // like the PDP gallery (its defaults are disabled above).
            this.lightbox.on('uiRegister', () => {
                const lightboxUI = this.lightbox?.pswp?.ui;
                if (!lightboxUI) return;

                lightboxUI.registerElement({
                    name: 'close-zoom',
                    ariaLabel: 'Close video',
                    order: 2,
                    isButton: true,
                    html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
                    onClick: () => {
                        this.lightbox?.pswp?.close();
                    },
                });
            });

            this.lightbox.init();
        }

        /**
         * Starts or stops the playback of a slide. Activating sets the real embed URL (with
         * autoplay), deactivating restores about:blank — that stops playback reliably even
         * while PhotoSwipe keeps the slide mounted.
         */
        togglePlayback(content, active) {
            const iframe = content?.element?.querySelector('iframe[data-src]');
            if (!iframe) return;

            if (active) {
                const src = iframe.getAttribute('data-src');
                if (!src || iframe.getAttribute('src') === src) return;
                // NWF-202
                if (!window.NwfVideoConsent) return;
                this.cancelConsent = window.NwfVideoConsent.gate(
                    iframe.parentElement,
                    'vimeo',
                    (byClick) => {
                        this.cancelConsent = null;
                        iframe.setAttribute('src', src);
                        if (byClick) iframe.focus();
                    },
                    { image: true },
                );
            } else {
                if (this.cancelConsent) {
                    this.cancelConsent();
                    this.cancelConsent = null;
                }
                if (iframe.getAttribute('src') !== 'about:blank') {
                    iframe.setAttribute('src', 'about:blank');
                }
            }
        }
    }

    customElements.define('nwf-my-videos', NwfMyVideos);
})();
